import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  Auth, 
  setPersistence, 
  browserLocalPersistence,
  inMemoryPersistence,
  indexedDBLocalPersistence 
} from 'firebase/auth';
import { captureException } from '@/utils/sentry';

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
}

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;

/**
 * Detects if the current platform is iOS/Safari which has known IndexedDB issues
 */
const isIOSOrSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  
  const ua = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isSafari = /safari/.test(ua) && !/chrome/.test(ua);
  
  return isIOS || isSafari;
};

/**
 * Attempts to set Firebase Auth persistence with fallback options
 */
const setAuthPersistence = async (auth: Auth): Promise<void> => {
  const persistenceOptions = isIOSOrSafari() 
    ? [browserLocalPersistence, inMemoryPersistence]
    : [indexedDBLocalPersistence, browserLocalPersistence, inMemoryPersistence];
  
  for (const persistence of persistenceOptions) {
    try {
      await setPersistence(auth, persistence);
      return;
    } catch (error) {
      console.warn(`Failed to set persistence to ${persistence.type}:`, error);
    }
  }
  
  console.error('All persistence options failed, using default session persistence');
  captureException(new Error('Failed to set any Firebase Auth persistence'), {
    extra: {
      component: 'firebase',
      action: 'setAuthPersistence',
      isIOSOrSafari: isIOSOrSafari(),
    },
  });
};

/**
 * Initialize Firebase with retry logic and persistence control
 */
export const initializeFirebase = async (retryCount = 0): Promise<FirebaseApp> => {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000;
  
  if (!app) {
    try {
      const response = await fetch('/api/firebase-config');
      if (!response.ok) {
        throw new Error('Failed to fetch Firebase configuration');
      }
      const config: FirebaseConfig = await response.json();
      app = initializeApp(config);
      authInstance = getAuth(app);
      
      await setAuthPersistence(authInstance);
      
    } catch (error) {
      console.error('Firebase initialization error:', error);
      
      if (retryCount < MAX_RETRIES) {
        console.info(`Retrying Firebase initialization (${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
        return initializeFirebase(retryCount + 1);
      }
      
      captureException(error as Error, {
        extra: {
          component: 'firebase',
          action: 'initializeFirebase',
          retryCount,
          isIOSOrSafari: isIOSOrSafari(),
        },
      });
      
      throw error;
    }
  }
  return app;
};

/**
 * Get auth instance
 */
export const getFirebaseAuth = () => {
  if (!authInstance) {
    throw new Error('Firebase must be initialized before getting auth');
  }
  return authInstance;
};

/**
 * Export a function to get auth instead of the instance directly
 */
export const auth = getFirebaseAuth;

/**
 * Check if IndexedDB is available and healthy
 */
export const isIndexedDBHealthy = async (): Promise<boolean> => {
  try {
    const testDB = indexedDB.open('__test__', 1);
    
    return new Promise((resolve) => {
      testDB.onsuccess = () => {
        if (testDB.result) {
          testDB.result.close();
          indexedDB.deleteDatabase('__test__');
        }
        resolve(true);
      };
      
      testDB.onerror = () => resolve(false);
      testDB.onblocked = () => resolve(false);
      
      setTimeout(() => resolve(false), 2000);
    });
  } catch (error) {
    return false;
  }
}; 